import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Dialog,
  DialogContent,
  CircularProgress,
  IconButton,
  Popover,
} from "@mui/material";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Cookie from "js-cookie";
import {
  StyledButton,
  StyledAgGridContainer,
  StyledSnackbar,
  StyledAlert,
} from "components/StyledComponents/styledComponents";
import URLs from "constants/urls";
import { useMaterialUIController } from "context";
import { StyledCancelButton } from "components/StyledComponents/styledComponents";

export default function TotalExpenditureStatement() {
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 2);
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const formatDate = (date) => date.toISOString().split("T")[0];

  // State
  const [startDate, setStartDate] = useState(formatDate(startOfMonth));
  const [endDate, setEndDate] = useState(formatDate(endOfMonth));
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [footerData, setFooterData] = useState([]);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverData, setPopoverData] = useState([]);
  const [popoverFooterData, setPopoverFooterData] = useState([]);
  const [popoverLedger, setPopoverLedger] = useState("");

  // Refs
  const gridRef = useRef(null);
  const popoverGridRef = useRef(null);
  const anchorRef = useRef(null);

  // Context and Token
  const jwt_token = Cookie.get("jwt_token");
  const [controller] = useMaterialUIController();
  const { user } = controller;

  // Fetch main expenditure data
  const fetchExpenditureData = async () => {
    if (!startDate || !endDate) {
      showAlert("Please select both start and end dates", "error");
      return;
    }
    setLoading(true);
    try {
      const url = `${URLs.totaleExpenditureStatement}startDate=${startDate}&endDate=${endDate}&companyId=${user.companyId}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });
      const data = await response.json();
      setRowData(data);
    } catch (error) {
      showAlert(`Error fetching data: ${error.message}`, "error");
    }
    setLoading(false);
  };
  const downloadExcel = (data) => {
    const worksheetData = data.map((item, index) => ({
      "S.No": index + 1,
      "Ledger Name": item.accountLedgerName,
      Amount: item.amount,
      Remarks: item.remarks,
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Expenses");

    // Column width for spacing
    const wscols = [
      { wch: 5 }, // S.No
      { wch: 30 }, // Ledger Name
      { wch: 12 }, // Amount
      { wch: 40 }, // Remarks
    ];
    worksheet["!cols"] = wscols;

    XLSX.writeFile(workbook, "Total_Expenses.xlsx");
  };

  const downloadPDF = (data) => {
    const doc = new jsPDF();

    // Heading
    doc.setFontSize(16);
    doc.text(`Total Expenses from ${startDate} to ${endDate}`, 14, 20);

    // Table columns
    const tableColumn = ["S.No", "Ledger Name", "Amount", "Remarks"];
    const tableRows = [];

    data.forEach((item, index) => {
      tableRows.push([index + 1, item.accountLedgerName, item.amount, item.remarks]);
    });

    doc.autoTable({
      startY: 30,
      head: [tableColumn],
      body: tableRows,
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [22, 160, 133],
        textColor: 255,
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 30 }, // S.No
        1: { cellWidth: 60 }, // Ledger Name
        2: { cellWidth: 25 }, // Amount
        3: { cellWidth: 70 }, // Remarks
      },
    });

    doc.save("Total_Expenses.pdf");
  };

  const fetchIndividualExpenditureData = async () => {
    setLoading(true);
    try {
      const url = `${URLs.totalExpenses}?startDate=${startDate}&endDate=${endDate}&companyId=${user.companyId}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });
      const data = await response.json();

      if (data && data.length > 0) {
        downloadExcel(data);
        downloadPDF(data);
      } else {
        showAlert(`No data found for given range`, "info");
      }
    } catch (error) {
      showAlert(`Error fetching data: ${error.message}`, "error");
    }
    setLoading(false);
  };
  // Alert helper
  const showAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };
  const handleCloseAlert = () => {
    setOpen(false);
  };
  // Initial fetch
  useEffect(() => {
    fetchExpenditureData();
  }, []);
  // Calculate footer for main grid
  useEffect(() => {
    const totalExpenditure = rowData.reduce((sum, row) => sum + (row.amount || 0), 0);
    setFooterData([
      {
        accountLedgerName: "Total Expenditure",
        amount: totalExpenditure,
        view: 10,
      },
    ]);
  }, [rowData]);

  // Fetch Popover details and update grid
  const handleViewDetails = async (accountLedgerName) => {
    setLoading(true);
    try {
      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      };
      const response = await fetch(
        `${URLs.totalListOfExpenses}startDate=${startDate}&endDate=${endDate}&companyId=${
          user.companyId
        }&actledName=${encodeURIComponent(accountLedgerName)}`,
        options
      );
      const data = await response.json();
      setPopoverData(data);

      // Calculate Popover footer
      const totalExpenditure = data.reduce((sum, row) => sum + (row.amount || 0), 0);
      setPopoverFooterData([
        {
          remarks: "Total Expenditure",
          amount: totalExpenditure,
        },
      ]);
    } catch (error) {
      showAlert(`Unable to fetch data: ${error.message}`, "error");
    }
    setAnchorEl(anchorRef.current);
    setPopoverLedger(accountLedgerName);
    setLoading(false);
  };

  // Close Popover
  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverData([]);
    setPopoverLedger("");
    setPopoverFooterData([]);
  };

  const columns = [
    { headerName: "Particulars", field: "accountLedgerName", filter: true, flex: 1 },
    {
      headerName: "Amount",
      field: "amount",
      aggFunc: "sum",
      maxWidth: 300,
      filter: true,
      cellClass: "text-right",
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) =>
        `₹ ${(params.value || 0).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
    {
      headerName: "View",
      field: "view",
      maxWidth: 100,
      filter: true,
      cellStyle: { display: "flex", justifyContent: "center", alignItems: "center" },
      cellRenderer: (params) =>
        params.data.view === 10 ? null : (
          <IconButton
            ref={anchorRef}
            onClick={() => handleViewDetails(params.data.accountLedgerName)}
          >
            <VisibilityIcon color="primary" />
          </IconButton>
        ),
    },
  ];

  // Popover grid columns
  const popoverGridColumns = [
    { headerName: "Expense Name", field: "remarks", flex: 2, filter: true },
    {
      headerName: "Amount",
      field: "amount",
      filter: true,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) =>
        `₹ ${(params.value || 0).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
  ];

  return (
    <DashboardLayout>
      <Dialog open={loading} onClose={() => {}} aria-labelledby="loading-dialog-title">
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" style={{ marginTop: "20px" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>

      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <StyledAlert onClose={handleCloseAlert} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>

      <Paper sx={{ padding: 2, marginBottom: 2, mt: 2, mr: 2 }}>
        <Typography variant="h6">Total Expenditure Statement</Typography>
        <Grid container spacing={2} alignItems="center" mt={2}>
          <Grid item xs={12} sm={5} md={3} lg={2}>
            <TextField
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={5} md={3} lg={2}>
            <TextField
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <StyledButton variant="contained" onClick={fetchExpenditureData}>
              Display
            </StyledButton>
            <StyledButton
              sx={{ ml: 2 }}
              variant="contained"
              onClick={fetchIndividualExpenditureData}
            >
              Print Total Expenses
            </StyledButton>
          </Grid>
        </Grid>
      </Paper>

      <StyledAgGridContainer
        component={Paper}
        className="ag-theme-alpine"
        sx={{
          flexGrow: 1,
          pr: 2,
          height: "100%",
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columns}
          enableCellTextSelection={true}
          pagination={true}
          paginationPageSize={10}
          pinnedBottomRowData={footerData}
          animateRows={true}
        />
      </StyledAgGridContainer>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          style: {
            width: "600px",
            height: "70vh",
            padding: "10px",
            backgroundColor: "#FFFFFF",
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            maxHeight: "70vh",
          },
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#FFFFFF",
            marginBottom: 8,
          }}
        >
          <Typography
            fontWeight="bold"
            sx={{ fontSize: "1rem", borderBottom: "4px solid #E59400", display: "inline" }}
          >
            {popoverLedger} - Expenses Details
          </Typography>
          <StyledCancelButton onClick={handlePopoverClose}>Close</StyledCancelButton>
        </div>

        <StyledAgGridContainer style={{ flexGrow: 1, width: "100%" }} className="ag-theme-alpine">
          <AgGridReact
            ref={popoverGridRef}
            columnDefs={popoverGridColumns}
            rowData={popoverData}
            pagination={true}
            paginationPageSize={10}
            suppressRowClickSelection
            suppressMovableColumns={true}
            pinnedBottomRowData={popoverFooterData}
            enableCellTextSelection={true}
            animateRows={true}
          />
        </StyledAgGridContainer>
      </Popover>
    </DashboardLayout>
  );
}
